import { Suspense, lazy } from "react"
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom"
import { AppLayout } from "@/components/app-layout"
import { RedirectAlert } from "@/components/redirect-alert"
import { SettingsLayout } from "@/pages/settings/settings-layout"
import { ProtectedRoute } from "@/auth/ProtectedRoute"
import { UserProvider } from "@/auth/UserContext"
import { RootRedirect } from "@/workspaces/RootRedirect"
import { ProtectedWorkspaceRoute } from "@/workspaces/ProtectedWorkspaceRoute"

const DashboardPage = lazy(() =>
  import("@/pages/dashboard-page").then((module) => ({
    default: module.DashboardPage,
  })),
)
const InboxPage = lazy(() =>
  import("@/pages/inbox-page").then((module) => ({
    default: module.InboxPage,
  })),
)
const ProjectsPage = lazy(() => import("@/pages/projects-page"))
const ProjectPage = lazy(() => import("@/pages/project-page"))
const ProjectSettingsPage = lazy(() => import("@/pages/project-settings-page"))
const TasksPage = lazy(() => import("@/pages/tasks-page"))
const TaskPage = lazy(() => import("@/pages/task-page"))
const FilesPage = lazy(() => import("@/pages/files-page"))
const ReportsPage = lazy(() => import("@/pages/reports-page"))
const ReportDashboardPage = lazy(() => import("@/pages/report-dashboard-page"))
const MeetingsRouteGuard = lazy(() =>
  import("@/modules/meetings").then((module) => ({
    default: module.MeetingsRouteGuard,
  })),
)
const MeetingsListPage = lazy(() =>
  import("@/modules/meetings").then((module) => ({
    default: module.MeetingsListPage,
  })),
)
const MeetingDetailPage = lazy(() =>
  import("@/modules/meetings").then((module) => ({
    default: module.MeetingDetailPage,
  })),
)
const SettingsMeetingsPage = lazy(() =>
  import("@/pages/settings/settings-meetings-page").then((module) => ({
    default: module.SettingsMeetingsPage,
  })),
)
const SupportPage = lazy(() =>
  import("@/pages/support-page").then((module) => ({
    default: module.SupportPage,
  })),
)
const TestPage = lazy(() =>
  import("@/pages/test-page").then((module) => ({
    default: module.TestPage,
  })),
)
const CallbackPage = lazy(() =>
  import("@/pages/callback-page").then((module) => ({
    default: module.CallbackPage,
  })),
)
const InviteAcceptPage = lazy(() =>
  import("@/pages/invite-accept-page").then((module) => ({
    default: module.InviteAcceptPage,
  })),
)
const SettingsGeneralPage = lazy(() =>
  import("@/pages/settings/settings-general-page").then((module) => ({
    default: module.SettingsGeneralPage,
  })),
)
const SettingsProfilePage = lazy(() =>
  import("@/pages/settings/settings-profile-page").then((module) => ({
    default: module.SettingsProfilePage,
  })),
)
const SettingsNotificationsPage = lazy(() =>
  import("@/pages/settings/settings-notifications-page").then((module) => ({
    default: module.SettingsNotificationsPage,
  })),
)
const SettingsMembersPage = lazy(() =>
  import("@/pages/settings/settings-members-page").then((module) => ({
    default: module.SettingsMembersPage,
  })),
)
const SettingsTeamsPage = lazy(() =>
  import("@/pages/settings/settings-teams-page").then((module) => ({
    default: module.SettingsTeamsPage,
  })),
)
const SettingsStatusSchemesPage = lazy(() =>
  import("@/pages/settings/settings-status-schemes-page").then((module) => ({
    default: module.SettingsStatusSchemesPage,
  })),
)
const SettingsRolesPage = lazy(() =>
  import("@/pages/settings/settings-roles-page").then((module) => ({
    default: module.SettingsRolesPage,
  })),
)
const SettingsStoragePage = lazy(() =>
  import("@/pages/settings/settings-storage-page").then((module) => ({
    default: module.SettingsStoragePage,
  })),
)
const SettingsBillingPage = lazy(() =>
  import("@/pages/settings/settings-billing-page").then((module) => ({
    default: module.SettingsBillingPage,
  })),
)
const OnboardingPage = lazy(() =>
  import("@/workspaces/OnboardingPage").then((module) => ({
    default: module.OnboardingPage,
  })),
)

function RouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
      Загрузка...
    </div>
  )
}

// Wraps everything that requires auth + user context
function AuthLayout() {
  return (
    <ProtectedRoute>
      <UserProvider>
        <Outlet />
      </UserProvider>
    </ProtectedRoute>
  )
}

function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Public */}
          <Route path="/callback" element={<CallbackPage />} />
          <Route path="/invite/:token" element={<InviteAcceptPage />} />

          {/* Auth-required */}
          <Route element={<AuthLayout />}>
            <Route index element={<RootRedirect />} />
            <Route path="/onboarding" element={<OnboardingPage />} />

            {/* Workspace-scoped */}
            <Route path="/:workspace_slug" element={<ProtectedWorkspaceRoute />}>
              <Route path="profile" element={<Navigate to="settings/profile" replace />} />

              {/* Main app (with sidebar) */}
              <Route element={<AppLayout />}>
                <Route index element={<Navigate to="home" replace />} />
                <Route path="home" element={<DashboardPage />} />
                <Route path="inbox" element={<InboxPage />} />
                <Route path="tasks" element={<TasksPage />} />
                <Route path="tasks/:taskId" element={<TaskPage />} />
                <Route element={<MeetingsRouteGuard />}>
                  <Route path="meetings" element={<MeetingsListPage />} />
                  <Route path="meetings/:meetingId" element={<MeetingDetailPage />} />
                </Route>
                <Route path="projects" element={<ProjectsPage />} />
                <Route path="projects/:projectId" element={<ProjectPage />} />
                <Route path="projects/:projectId/settings" element={<ProjectSettingsPage />} />
                <Route path="files" element={<FilesPage />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="reports/:dashboardId" element={<ReportDashboardPage />} />
                <Route path="calendar" element={<TestPage />} />
                <Route path="notifications" element={<TestPage />} />
                <Route path="team" element={<TestPage />} />
                <Route path="support" element={<SupportPage />} />
                <Route path="test" element={<TestPage />} />
                <Route
                  path="*"
                  element={
                    <RedirectAlert
                      title="Страница не найдена"
                      description="Такой страницы нет или ссылка устарела. Сейчас вернем вас на главную страницу воркспейса."
                      actionLabel="На главную"
                      to="home"
                    />
                  }
                />
              </Route>

              {/* Settings — own full-page layout, no main sidebar */}
              <Route path="settings" element={<SettingsLayout />}>
                <Route index element={<Navigate to="profile" replace />} />
                <Route path="profile" element={<SettingsProfilePage />} />
                <Route path="notifications" element={<SettingsNotificationsPage />} />
                <Route path="general" element={<SettingsGeneralPage />} />
                <Route path="members" element={<SettingsMembersPage />} />
                <Route path="teams" element={<SettingsTeamsPage />} />
                <Route path="status-schemes" element={<SettingsStatusSchemesPage />} />
                <Route path="roles" element={<SettingsRolesPage />} />
                <Route path="storage" element={<SettingsStoragePage />} />
                <Route path="billing" element={<SettingsBillingPage />} />
                <Route path="meetings" element={<SettingsMeetingsPage />} />
                <Route
                  path="*"
                  element={
                    <RedirectAlert
                      title="Раздел настроек не найден"
                      description="Такого раздела настроек нет или ссылка устарела. Сейчас вернем вас на главную страницу."
                      actionLabel="На главную"
                      to="../home"
                    />
                  }
                />
              </Route>
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}

export default App
